import { useCallback, useEffect, useState } from 'react';
import mask from 'assets/photo_generation_gap.svg';
import premium_pic from 'assets/purple_crown.svg';
import video_gen_icon from 'assets/video_gen_icon.svg';
import { SSEEventType } from 'constants/sse';
import CommonHelper from "helpers/CommonHelper";
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import useModalState from 'hooks/useModalState';
import useSSEEvent from 'hooks/useSSEEvent';
import { isNil } from 'ramda';
import { useMappedState } from 'redux-react-hook';
import { useLazyGetCurrentUserQuery } from "services/api/usersApi";
import styled, { css, keyframes } from "styled-components";

import BlurOverlay from '../BlurOverlay';
import ConfirmVideoGenerationModal from '../ConfirmVideoGenerationModal';


const CONTAINER_WIDTH = 320;


const PictureMessage = (props) => {
  const {
    xUrl,
    url,
    shouldBlur,
    onZoomClick,
    onUnblurClick,
    scenario,
    game,
    dialog,
    batchId,
    setBatches
  } = props;

  const [hasError, setHasError] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageRatio, setImageRatio] = useState(null);
  const [isVisibleConfirmVideoGenerationModal, openConfirmVideoGenerationModal, closeConfirmVideoGenerationModal] = useModalState(false);

  const { data: photoGenerationData } = useSSEEvent(SSEEventType.CUSTOM_DIALOG_PHOTO_GEN);

  const [fetchUser] = useLazyGetCurrentUserQuery();

  const isVideoGenerationEnabled = useFeatureFlag('isVideoGenerationEnabled');

  const { hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        hasSubscription: hasSubscription
      };
    }, [])
  );

  const isPrivateScenario = !game.isPublic;
  const isVisiblePremiumPicture = hasSubscription && !hasError && !isPrivateScenario;

  const isPendingPhotoGeneration = !imageUrl || hasError;
  const isVisibleVideoGenButton = isVideoGenerationEnabled && isPrivateScenario && !isPendingPhotoGeneration && !isNil(imageRatio);

  let containerHeight = CONTAINER_WIDTH;

  if (imageRatio && !isPendingPhotoGeneration) {
    containerHeight = Math.round(CONTAINER_WIDTH / imageRatio);
  }

  const getUrlBySubscription = () => {
    if (hasSubscription || !shouldBlur) {
      return CommonHelper.getFilesCdnUrl(xUrl);
    } else {
      return CommonHelper.getFilesCdnUrl(url);
    }
  };



  const handleImageError = (e) => {
    e.target.src = mask;
    setHasError(true);
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    if (img.naturalWidth && img.naturalHeight) {
      setImageRatio(img.naturalWidth / img.naturalHeight);
    } else {
      setImageRatio(1);
    }
  };

  useEffect(() => {
    const isCompletedMediaGenerationStatus = photoGenerationData?.status === 'completed';
    if (isCompletedMediaGenerationStatus) {
      setHasError(false);
      fetchUser();
      const newUrl = CommonHelper.getFilesCdnUrl(xUrl);
      setImageUrl(newUrl + '?t=' + new Date().getTime());
    }
  }, [photoGenerationData, xUrl, fetchUser]);

  useEffect(() => {
    if (!imageUrl) {
      setImageUrl(getUrlBySubscription());
    }
  }, [xUrl, url, hasSubscription, shouldBlur]);

  useEffect(() => {
    if (imageUrl && !imageUrl.includes('mask.svg')) {
      setHasError(false);
    }
  }, [imageUrl]);

  return (
    <>
      <MessageContainer $isVisibleVideoGenButton={isVisibleVideoGenButton} >
        <FlexContainer>
          <PictureContainer isPrivateScenario={isPrivateScenario}>
            <ImageWrapper
              isPendingPhotoGeneration={isPendingPhotoGeneration}
              style={{ width: `${CONTAINER_WIDTH}px`, height: `${isPendingPhotoGeneration ? CONTAINER_WIDTH : containerHeight}px`, transition: 'height 0.2s' }}
            >
              <PictureInnerPlaceholder
                key={imageUrl}
                src={imageUrl || getUrlBySubscription()}
                alt="narrator image"
                onError={handleImageError}
                onLoad={!isPendingPhotoGeneration ? handleImageLoad : undefined}
              />
              {hasSubscription || !shouldBlur ? (
                <ZoomPictureButton onClick={() => !isPendingPhotoGeneration && onZoomClick(xUrl)} />
              ) : (
                <BlurOverlay url={url} onUnblurClick={onUnblurClick} scenario={scenario} game={game} dialog={dialog} />
              )}
              {isVisiblePremiumPicture && (
                <PremiumBadge>
                  <img src={premium_pic} alt="Premium" />
                </PremiumBadge>
              )}
              {isPendingPhotoGeneration && (
                <>
                  <GeneratingText>
                    Generating a pic...
                  </GeneratingText>
                  <GeneratingSubText>
                    This may take 15 seconds to a few minutes.
                  </GeneratingSubText>
                </>
              )}
            </ImageWrapper>
            {isVisibleVideoGenButton && (
              <CreateVideoButton onClick={() => openConfirmVideoGenerationModal()}>
                <img src={video_gen_icon} alt="Create Video" />
                <span>Create Video</span>
              </CreateVideoButton>
            )}
          </PictureContainer>
        </FlexContainer>
      </MessageContainer>
      {isVisibleConfirmVideoGenerationModal && (
        <ConfirmVideoGenerationModal
          isVisible
          onClose={closeConfirmVideoGenerationModal}
          batchId={batchId}
          setBatches={setBatches}
        />
      )}
    </>
  );
};

export default PictureMessage;

const shimmerAnimation = keyframes`
  0% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  47% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  53% {
    transform: translate(100%, 100%) rotate(45deg);
    opacity: 1;
  }
  95% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 1;
  }
  100% {
    transform: translate(-100%, -100%) rotate(45deg);
    opacity: 0;
  }
`;


const PictureContainer = styled.div`
  position: relative;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1.5rem;

  ${props => props.isPrivateScenario && css`
    margin-top: 0;
  `}
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  width: 320px;
  background: #131313;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: height 0.2s;
  
  ${props => props.isPendingPhotoGeneration && css`
    &::after {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 40%,
        rgba(255, 255, 255, 0.04) 47%,
        rgba(255, 255, 255, 0.07) 50%,
        rgba(255, 255, 255, 0.04) 53%,
        transparent 60%,
        transparent 100%
      );
      transform: translate(-100%, -100%) rotate(45deg);
      animation: ${shimmerAnimation} 4s cubic-bezier(0.4, 0.0, 0.2, 1) infinite;
      pointer-events: none;
    }
  `}
`;

const PictureInnerPlaceholder = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  position: relative;
  object-fit: contain;
`;

const ZoomPictureButton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(min(75vh, 75vw, 320px));
  height: calc(min(75vh, 75vw, 320px));
  border-radius: 12px;
  cursor: pointer;
`;

const GeneratingText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #818181;
  width: 100%;
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 18px;
  line-height: 20px;
  text-align: center;
`;

const GeneratingSubText = styled.div`
  position: absolute;
  top: 60%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #696969;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  width: 80%;
  line-height: 16px;
`;

const PremiumBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
`;

const CreateVideoButton = styled.button`
  position: absolute;
  bottom: -30px;
  right: 0;
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;

  img {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 768px) {
    bottom: -26px;
  }

  span {
    color: white;
    font-family: 'Nunito Sans';
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    background: linear-gradient(135deg, #9E5EEE 0%, #B759EA 32%, #C65DE9 66%, #B885F4 100%);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const MessageContainer = styled.div`
  margin-top: 1.5rem;
  
  ${props => props.$isVisibleVideoGenButton && css`
    margin-bottom: 1.5rem;
  `}
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: center;
`;

